import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { X, Mail } from "lucide-react";

const Field = ({ label, children }) => (
  <label className="block">
    <div className="label-mono mb-1">{label}</div>
    {children}
  </label>
);

const CURRENCY_SYM = { USD: "$", INR: "₹", EUR: "€", GBP: "£" };

/**
 * "Send Invoice" composer. Pulled out of Invoices.jsx for testability and to keep the
 * parent file under the Babel AST modal-clash threshold we hit earlier.
 */
export default function SendEmailModal({ inv, senders, client, onClose, onSent, onAddSender }) {
  const [senderId, setSenderId] = useState(senders[0]?.id || "");
  const [to, setTo] = useState(client?.email || "");
  const [cc, setCc] = useState("");
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState("");
  const sym = CURRENCY_SYM[inv.currency] || "";
  const defaultSubject = `Invoice ${inv.invoice_number} — ${inv.client_name || ""}`;
  const defaultBody = (() => {
    const sender = senders.find((s) => s.id === senderId);
    return (
      `Dear ${inv.client_name || "Client"},\n\n` +
      `Please find attached invoice ${inv.invoice_number} for the period ${inv.period_start} → ${inv.period_end}.\n\n` +
      `Total Amount: ${sym}${(inv.total_amount || 0).toFixed(2)} (${inv.currency || "INR"})\n\n` +
      `Kindly arrange payment at your earliest convenience.\n\n` +
      `Regards,\n${sender?.display_name || sender?.email || ""}`
    );
  })();
  const [subject, setSubject] = useState(defaultSubject);
  const [body, setBody] = useState(defaultBody);
  const [attachPdf, setAttachPdf] = useState(true);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/invoice-email-templates").then((r) => setTemplates(r.data || [])).catch(() => {});
  }, []);

  const applyTemplate = async (tplId) => {
    setTemplateId(tplId);
    if (!tplId) return;
    try {
      const { data } = await api.post(`/invoice-email-templates/${tplId}/preview`, {
        invoice_id: inv.id,
        sender_id: senderId,
      });
      setSubject(data.subject || "");
      setBody(data.body || "");
    } catch { toast.error("Could not load template"); }
  };

  useEffect(() => {
    const sender = senders.find((s) => s.id === senderId);
    if (sender && body === defaultBody) {
      setBody(defaultBody);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderId]);

  const send = async (e) => {
    e.preventDefault();
    if (!senderId) { toast.error("Choose a sender account"); return; }
    if (!to) { toast.error("Recipient is required"); return; }
    setSending(true);
    try {
      await api.post(`/invoices/${inv.id}/send-email`, {
        sender_id: senderId,
        to, cc,
        subject,
        body,
        attach_pdf: attachPdf,
      });
      toast.success(`Invoice sent to ${to}`);
      onSent?.();
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Send failed");
    }
    setSending(false);
  };

  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <form onSubmit={send} className="brut-card max-w-2xl w-full max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 pt-6 pb-3 border-b border-zinc-200 bg-white shrink-0">
          <div>
            <div className="label-mono">SEND INVOICE</div>
            <h2 className="font-heading text-2xl font-black">{inv.invoice_number}</h2>
            <div className="text-xs font-mono text-zinc-600">To: {inv.client_name} · Total {sym}{(inv.total_amount || 0).toFixed(2)} {inv.currency}</div>
          </div>
          <button type="button" onClick={onClose} className="brut-btn-secondary p-1.5 ml-3"><X className="w-4 h-4" /></button>
        </div>

        <div className="px-6 py-4 overflow-y-auto flex-1 space-y-3">
          {senders.length === 0 ? (
            <div className="border border-yellow-500 bg-yellow-50 p-4 text-sm font-mono">
              No invoice email sender configured yet.
              <button type="button" onClick={onAddSender} className="ml-2 underline font-bold" data-testid="add-sender-from-email">Add one now →</button>
            </div>
          ) : (
            <Field label="SEND FROM">
              <select value={senderId} onChange={(e) => setSenderId(e.target.value)} className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="email-sender-select">
                {senders.map((s) => <option key={s.id} value={s.id}>{s.display_name} &lt;{s.email}&gt;</option>)}
              </select>
            </Field>
          )}

          <Field label="TEMPLATE (apply preset subject + body)">
            <select
              value={templateId}
              onChange={(e) => applyTemplate(e.target.value)}
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              data-testid="email-template-select"
            >
              <option value="">— manual / no template —</option>
              {templates.map((t) => (
                <option key={t.id} value={t.id}>{t.name}{t.status_trigger ? ` · ${t.status_trigger}` : ""}</option>
              ))}
            </select>
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="TO">
              <input required type="email" multiple value={to} onChange={(e) => setTo(e.target.value)} placeholder="client@example.com" className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="email-to-input" />
            </Field>
            <Field label="CC (optional, comma-separated)">
              <input value={cc} onChange={(e) => setCc(e.target.value)} placeholder="boss@yourorg.com" className="brut-input w-full px-3 py-2 text-sm font-mono" data-testid="email-cc-input" />
            </Field>
          </div>

          <Field label="SUBJECT">
            <input value={subject} onChange={(e) => setSubject(e.target.value)} className="brut-input w-full px-3 py-2 text-sm" data-testid="email-subject-input" />
          </Field>

          <Field label="BODY">
            <textarea
              rows={9}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              className="brut-input w-full px-3 py-2 text-sm font-mono"
              data-testid="email-body-input"
            />
          </Field>

          <label className="flex items-center gap-2 text-sm font-mono">
            <input type="checkbox" checked={attachPdf} onChange={(e) => setAttachPdf(e.target.checked)} data-testid="email-attach-pdf" />
            ATTACH INVOICE PDF
          </label>
        </div>

        <div className="flex justify-end gap-2 px-6 py-3 border-t border-zinc-300 bg-white shrink-0">
          <button type="button" onClick={onClose} className="brut-btn-secondary px-4 py-2 text-sm">CANCEL</button>
          <button type="submit" disabled={sending || senders.length === 0} className="brut-btn px-4 py-2 text-sm flex items-center gap-2 disabled:opacity-60" data-testid="send-email-submit">
            <Mail className="w-4 h-4" /> {sending ? "SENDING…" : "SEND EMAIL"}
          </button>
        </div>
      </form>
    </div>
  );
}
